/**
 * Restaurant → Social Media (FNF-1609): the restaurant's Fynkle Wall Profile
 * and the posts it publishes to the Wall, from social-networking-service's
 * `/v2/business/social/restaurants/{restaurantId}/...` routes. Responses use
 * the business envelope `{status_code, message, data, error}`.
 */

export interface SocialEnvelope<T> {
  status_code: number;
  message: string;
  data: T;
  error?: any;
}

/** The restaurant's Wall Profile. Name and logo come from Restaurant Profile. */
export interface RestaurantWallProfile {
  restaurantId: string;
  username: string;
  displayName: string;
  bio?: string | null;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  rating?: number | null;
  isVerified: boolean;
  followersCount: number;
  moments: number;
  karma: number;
  isHidden: boolean;
}

/** PUT .../profile (FNF-1683): only the fields present change; null clears. */
export interface RestaurantWallProfileUpdate {
  username?: string;
  bio?: string | null;
  coverImageUrl?: string | null;
}

/** The server's handle rule (social-networking-service `utils/username.py`). */
export const HANDLE_PATTERN = /^[a-z0-9_.]{3,30}$/;
export const TAGLINE_MAX = 300;

/** Trims, drops a leading "@" and lowercases, as the server does before it
 * checks the pattern: "@VelvetScoop " -> "velvetscoop". */
export function normalizeHandle(raw: string): string {
  return (raw || '').trim().replace(/^@/, '').toLowerCase();
}

/** A presigned POST for one post photo (FNF-1688). */
export interface PostPhotoUpload {
  url: string;
  fields: Record<string, string>;
  objectUrl: string;
  expiresIn: number;
  maxBytes: number;
}

/** What the upload endpoint accepts; anything else is refused before upload. */
export const POST_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const MAX_POST_PHOTOS = 10;

/** What a restaurant may post as; reviews and check-ins are a diner's. */
export type RestaurantPostType = 'general' | 'photo' | 'recommendation';

/** unchecked → being reviewed; cleared / restored → live; flagged / removed → not shown. */
export type ModerationStatus = 'unchecked' | 'cleared' | 'flagged' | 'removed' | 'restored';

export interface RestaurantPost {
  postId: string;
  type: RestaurantPostType | string;
  content: string;
  mediaUrls: string[];
  tags: string[];
  likesCount: number;
  commentsCount: number;
  inspiredCount: number;
  created: string;
  editedAt?: string | null;
  postedAs: { type: string; id: string; name?: string | null; imageUrl?: string | null };
  moderationStatus: ModerationStatus | string;
  moderationReason?: string | null;
}

export interface RestaurantPostPage {
  items: RestaurantPost[];
  nextCursor?: string | null;
}

/** Who wrote a comment: a diner, or the restaurant itself. */
export interface CommentAuthor {
  userId: string;
  username?: string | null;
  displayName?: string | null;
  profileImageUrl?: string | null;
}

/** A diner's comment or reply on a restaurant post (FNF-1697). */
export interface PostComment {
  interactionId: string;
  postId: string;
  author: CommentAuthor;
  content: string;
  created: string;
  /** Replies: the top-level comment they belong to. */
  parentCommentId?: string | null;
  /** A reply to a reply: whom it answers ("@name"). */
  replyTo?: CommentAuthor | null;
  likesCount: number;
  /** Top-level comments only: replies to load with getCommentReplies. */
  replyCount: number;
  /** Written by the post's author, i.e. the restaurant. */
  isPostAuthor: boolean;
  /** "restaurant" when the restaurant wrote it (FNF-1698). */
  authorType?: 'user' | 'restaurant';
}

/** A comment as the restaurant, or a reply with parentCommentId (FNF-1698). */
export interface PostCommentCreate {
  content: string;
  parentCommentId?: string;
}

/** The service's comment length limit. */
export const COMMENT_MAX = 1000;

export interface CommentRepliesPage {
  replies: PostComment[];
  hasMore: boolean;
  nextCursor?: string | null;
}

export interface RestaurantPostCreate {
  type: RestaurantPostType;
  content: string;
  mediaUrls?: string[];
  tags?: string[];
}

export type RestaurantPostUpdate = Partial<RestaurantPostCreate>;

export const POST_CONTENT_MAX = 2000;

export const POST_TYPE_LABELS: Record<string, string> = {
  general: 'Update',
  recommendation: 'Recommendation',
  photo: 'Photo',
};

/** How staff read a post's moderation state; diners only see live posts. */
export function moderationBadge(status: string): {
  label: string;
  tone: 'live' | 'review' | 'down';
} {
  switch (status) {
    case 'cleared':
    case 'restored':
      return { label: 'Live', tone: 'live' };
    case 'flagged':
      return { label: 'Under review', tone: 'review' };
    case 'removed':
      return { label: 'Removed', tone: 'down' };
    default:
      // Unchecked posts are shown on the Wall while the scan runs.
      return { label: 'Live · checking', tone: 'review' };
  }
}
